// Libraries
import moment from 'moment';
import React from 'react';
import BigCalendar from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Supermove
import {Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {UserCalendarEventModel} from '@supermove/models';

// App
import ErrorCatcher from '@shared/modules/Error/components/ErrorCatcher';
import {
  toBigCalendarView,
  toUserCalendarViewType,
  USER_CALENDAR_VIEW_TYPE,
} from '@shared/modules/UserCalendar/enums/UserCalendarViewType';

import './UserCalendar.css';

const localizer = BigCalendar.momentLocalizer(moment);

const Column = Styled.View`
  height: 700px;
`;

const UserCalendar = ({
  events,
  startDate,
  viewType,
  views,
  handleDateChange,
  handleViewChange,
  handleSelectSlot,
}: {
  events: UserCalendarEventModel[];
  startDate: Date;
  viewType: USER_CALENDAR_VIEW_TYPE;
  views: USER_CALENDAR_VIEW_TYPE[];
  handleDateChange?: (baseTime: number) => void;
  handleViewChange?: (viewType: USER_CALENDAR_VIEW_TYPE) => void;
  handleSelectSlot?: (slotInfo: {
    start: Date | string;
    end: Date | string;
    action: 'select' | 'click' | 'doubleClick';
  }) => void;
}) => {
  const calendarEvents = events.map((event) => ({
    id: event.id,
    title: event.title,
    start: new Date(event.startTime * 1000),
    end: new Date(event.endTime * 1000),
    allDay: event.whenType === 'DATE',
    resource: event,
  }));

  const handleNavigate = (date: Date) => {
    const newBaseTime = moment(date).unix();
    handleDateChange?.(newBaseTime);
  };

  const allViews = views.map(toBigCalendarView);
  const currentView = toBigCalendarView(viewType);

  return (
    <Column>
      <ErrorCatcher>
        <BigCalendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor='start'
          endAccessor='end'
          date={startDate}
          view={currentView}
          views={allViews}
          onView={(view) => handleViewChange?.(toUserCalendarViewType(view))}
          onNavigate={handleNavigate}
          onSelectSlot={handleSelectSlot}
          selectable
        />
      </ErrorCatcher>
    </Column>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
UserCalendar.fragment = gql`
  fragment UserCalendar on User {
    id
    calendarEvents(calendarId: $calendarId, startTime: $startTime, endTime: $endTime) {
      id
      calendarId
      busy
      description
      textDescription
      htmlLink
      readOnly
      status
      title
      whenType
      startTime
      endTime
      dateValue
      startDate
      endDate
    }
  }
`;

export default UserCalendar;
